import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardBody, CardFooter } from "../../components/card/card.jsx";
import { useParams, useNavigate } from "react-router-dom";
import { getAuthHeaders } from "../../misc.js";
import SongReelForm from "../../components/SongReelForm/SongReelForm.js";
import CommentsTable from "../../components/CommentsTable/CommentsTable.js";
import SongSelector from "../../components/SongSelector/SongSelector.js";
import AlignmentEditor from "../../components/AlignmentEditor/AlignmentEditor.js";
import ReelDetailsPreview from "../../components/ReelsDetailsPreview/ReelsDetailsPreview.js";
import ThumbnailUploader from "../../components/ThumbnailUploader/ThumbnailUploader.js";

const SongReelCreate = () => {
  // Main state
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [videoId, setVideoId] = useState("");
  const [commentsData, setCommentsData] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [channelLogo, setChannelLogo] = useState("");
  const [thumbnailBackgrounds, setThumbnailBackgrounds] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState("");
  const [title, setTitle] = useState("");
  const [songs, setSongs] = useState([]);
  const [songReelId, setSongReelId] = useState(null);
  const [isGeneratingSongs, setIsGeneratingSongs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [thumbnailImage, setThumbnailImage] = useState("");
  const [reelDetails, setReelDetails] = useState(null);
  const [thumbnailBackground, setThumbnailBackground] = useState(null);
  const [channelUsername, setChannelUsername] = useState(null);
  const [shortBackground, setShortBackground] = useState(null);
  const [stillFrame, setStillFrame] = useState(null);
  // Alignment preview state (now automatically generated)
  const [alignedComments, setAlignedComments] = useState([]);
  const [showAlignmentEditor, setShowAlignmentEditor] = useState(false);
  const [finalThumbnail, setFinalThumbnail] = useState(null);
  const [videoBackground, setVideoBackground] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  
 // Fetch channels.
 useEffect(() => {
  const fetchChannels = async () => {
    try {
      const response = await fetch("https://api.moatoverflow.com/v1/channel", {
        headers: { ...getAuthHeaders() }
      });
      const data = await response.json();
      const songReelsChannels = data.data.filter(
        (channel) => channel.type === "song_reel"
      );
      setChannels(songReelsChannels);

      // Only set default channel if one has not been preselected.
      if (!selectedChannel && songReelsChannels.length > 0) {
        setSelectedChannel(songReelsChannels[0]._id || songReelsChannels[0].name);
        setThumbnailBackgrounds(songReelsChannels[0].thumbnail_backgrounds || []);
        setChannelLogo(songReelsChannels[0].logo || "");
        setThumbnailBackground(songReelsChannels[0].thumbnail_backgrounds[0] || null);
        setVideoBackground(songReelsChannels[0].video_background || null);
        setSelectedGenre(songReelsChannels[0].genre || "");
        setChannelUsername(songReelsChannels[0].channel_username || null);
        setShortBackground(songReelsChannels[0].short_background || null);
      } else if (selectedChannel) {
        const selectedChannelData = songReelsChannels.find(
          (channel) => channel._id === selectedChannel
        );
        if (selectedChannelData) {
          setThumbnailBackgrounds(selectedChannelData.thumbnail_backgrounds || []);
          setChannelLogo(selectedChannelData.logo || "");
          setThumbnailBackground(selectedChannelData.thumbnail_backgrounds[0] || null);
          setVideoBackground(selectedChannelData.video_background || null);
          setSelectedGenre(selectedChannelData.genre || "");
          setChannelUsername(selectedChannelData.channel_username || null);
          setShortBackground(selectedChannelData.short_background || null);
        }
      }
    } catch (err) {
      console.error("Error fetching channels:", err);
      setError("Failed to load channels.");
    }
  };
  fetchChannels();
}, [selectedChannel]); // Optionally add selectedChannel as a dependency  

  // Load document if an id is provided.
  useEffect(() => {
    if (id) {
      const loadSongReelDocument = async () => {
        try {
          const response = await fetch(`https://api.moatoverflow.com/v1/song_reel/${id}`, {
            headers: { ...getAuthHeaders() }
          });
          const data = await response.json();
          if (data.success && data.data) {
            const doc = data.data;
            setTitle(doc.title || "");
            setSelectedGenre(doc.genre || "");
            setThumbnailImage(doc.still_frame || "");
            setChannelLogo(doc.logo || "");
            setSelectedChannel(doc.channelId);
            setThumbnailBackgrounds(doc.thumbnail_backgrounds || []);
            setThumbnailBackground(doc.thumbnail_background || null);
            setPlatform(doc.platform || "instagram");
            setFinalThumbnail(doc.thumbnail || null);
            setIsGeneratingSongs(doc.status === "generating_song" ? true : false)
            setVideoBackground(doc.video_background || null);        
            setSongs(
              doc.songs
                ? doc.songs.map((songItem, index) => ({
                    audio_url: songItem.audio_url,
                    selected: doc.selectedSongIndex === index,
                    // If you had alignment manually set earlier, ignore it now.
                    alignment: null,
                  }))
                : []
            );
            setShortBackground(doc.short_background || null);
            setVideoId(doc.video_id || "");
            setCommentsData({ comments: doc.comments });
            setSelectedRows(doc.comments ? doc.comments.map((_, index) => index) : []);
            setSongReelId(doc._id);
            setReelDetails(doc);
            setStillFrame(doc.still_frame || null);
            setChannelUsername(doc.channel_username || null);
            // If alignedComments exist in the loaded doc, show the preview.
            if (doc.alignedComments && doc.alignedComments.length > 0) {
              setAlignedComments(doc.alignedComments);
              setShowAlignmentEditor(true);
            }
          } else {
            setError("Song Reel document not found.");
          }
        } catch (err) {
          console.error("Error loading song reel document:", err);
          setError("Failed to load song reel document.");
        }
      };
      loadSongReelDocument();
    }
  }, [id]);

  // Poll for song generation if needed.
  useEffect(() => {
    let interval;
    if (isGeneratingSongs && songReelId) {
      interval = setInterval(async () => {
        try {
          const response = await fetch(`https://api.moatoverflow.com/v1/song_reel/${songReelId}`, {
            headers: { ...getAuthHeaders() }
          });
          const data = await response.json();
          if (data.data && data.data.songs && data.data.songs.length > 0) {
            const updatedSongs = data.data.songs.map((songItem) => ({
              audio_url: songItem.audio_url,
              selected: false,
              alignment: null,
            }));
            setSongs(updatedSongs);
            setIsGeneratingSongs(false);
            clearInterval(interval);
          }
        } catch (err) {
          console.error("Error polling song reel document:", err);
        }
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [isGeneratingSongs, songReelId]);

 
  const handleSelectChannel = (channelId) => {
    const selectedChannel = channels.find((channel) => channel._id === channelId);
    setSelectedChannel(selectedChannel._id);
    setThumbnailBackgrounds(selectedChannel.thumbnail_backgrounds || []);
    setChannelLogo(selectedChannel.logo || "");
    setThumbnailBackground(selectedChannel.thumbnail_backgrounds[0] || null);
    setVideoBackground(selectedChannel.video_background || null);
    setSelectedGenre(selectedChannel.genre || "");
    setChannelUsername(selectedChannel.channel_username || null);
    setShortBackground(selectedChannel.short_background || null);
  };

  const handleSelectThumbnailBackground = (background) => {
    setThumbnailBackground(background);
  };

  // Extract comments handler.
  const handleExtractComments = async () => {
    if (!videoId) {
      setError("Please enter a video ID/shortcode.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      let paginationParam = "";
      if (platform === "instagram" && commentsData && commentsData.next_min_id) {
        paginationParam = `?next_min_id=${commentsData.next_min_id}`;
      } else if (platform === "tiktok" && commentsData && commentsData.cursor) {
        paginationParam = `?cursor=${commentsData.cursor}`;
      }

      let url = "";
      if (platform === "instagram") {
        url = `https://api.moatoverflow.com/v1/instagram/${videoId}${paginationParam}`;
      } else if (platform === "tiktok") {
        url = `https://api.moatoverflow.com/v1/tiktok/${videoId}${paginationParam}`;
      }

      const response = await fetch(url, {
        headers: { ...getAuthHeaders() }
      });
      const data = await response.json();
      setCommentsData((prev) => ({
        comments: prev && prev.comments ? [...prev.comments, ...data.comments] : data.comments,
        ...(platform === "instagram" ? { next_min_id: data.next_min_id } : { cursor: data.cursor })
      }));
    } catch (err) {
      console.error("Error extracting comments:", err);
      setError("Failed to extract comments.");
    } finally {
      setLoading(false);
    }
  };

  // Toggle comment selection.
  const toggleRowSelection = (index) => {
    setSelectedRows((prevSelected) => {
      if (prevSelected.includes(index)) {
        return prevSelected.filter((i) => i !== index);
      } else {
        return [...prevSelected, index];
      }
    });
  };

  const handleGenerateSongFile = async () => {
    if (!commentsData || !commentsData.comments || selectedRows.length === 0) {
      setError("Please extract comments and select at least one comment.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const payload = {
        comments: commentsData.comments.filter((_, index) =>
          selectedRows.includes(index)
        ),
        video_id: videoId,
      };
      // Include title and genre if provided.
      if (title) payload.title = title;
      if (selectedGenre) payload.genre = selectedGenre;
  
      if (songReelId) {
        payload._id = songReelId;
      }
  
      const response = await fetch("https://api.moatoverflow.com/v1/song_reel/generate_song", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!id && data.data && data.data._id) {
        setSongReelId(data.data._id);
        navigate(`/create-song-reel/${data.data._id}`);
      } else if (data.data && data.data._id) {
        setSongReelId(data.data._id);
      }
      setIsGeneratingSongs(true);
    } catch (err) {
      console.error("Error generating song file:", err);
      setError("Failed to generate song file.");
    } finally {
      setLoading(false);
    }
  };

  const handlePostNewReel = async () => {
    if (!videoId) {
      setError("Please enter a video ID/shortcode for the reel.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const payload = {
        video_id: videoId,
        title: title,         // title is optional if not provided
        genre: selectedGenre, // genre is optional as well
        channelId: selectedChannel, // assuming selectedChannel represents the channel's ID
        platform: platform,   // assuming platform is either "instagram" or "tiktok"
        logo: channelLogo,    // assuming channelLogo is the channel's logo URL
        thumbnail_background: thumbnailBackground, // assuming thumbnailBackground is the selected background 
        video_background: videoBackground, // assuming videoBackground is the selected video background
        channel_username: channelUsername, // assuming channelUsername is the channel's username
        short_background: shortBackground, // assuming shortBackground is the selected short background
      };
  
      const response = await fetch("https://api.moatoverflow.com/v1/song_reel/new_reel", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success && data.data && data.data._id) {
        setSongReelId(data.data._id);
        setReelDetails(data.data);
        alert("New reel posted successfully!");
      } else {
        setError("Failed to post new reel.");
      }
    } catch (err) {
      console.error("Error posting new reel:", err);
      setError("Failed to post new reel.");
    } finally {
      setLoading(false);
    }
  };
  

  // Generate song reel.
  const handleGenerateSongReel = async () => {
    if (!selectedGenre) {
      setError("Please select a song genre.");
      return;
    }
    if (songs.length === 0) {
      setError("Please generate song files first.");
      return;
    }
    if (!commentsData || !commentsData.comments || selectedRows.length === 0) {
      setError("Please extract comments and select at least one comment.");
      return;
    }
    if (!title) {
      setError("Please enter a title.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const payload = {
        comments: commentsData.comments.filter((_, index) =>
          selectedRows.includes(index)
        ),
        genre: selectedGenre,
        title: title,
        songs: songs,
      };
      if (songReelId) {
        payload._id = songReelId;
      }
      const response = await fetch("https://api.moatoverflow.com/v1/song_reel/create", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to generate song reel");
      }
      const result = await response.json();
      if (result.data && result.data._id) {
        setSongReelId(result.data._id);
      }
      alert("Song Reel generated successfully!");
    } catch (err) {
      console.error("Error generating Song Reel:", err);
      setError("Failed to generate song reel.");
    } finally {
      setLoading(false);
    }
  };

  // Select a song.
  const handleSelectSong = async (index) => {
    const updatedSongs = songs.map((song, idx) => ({
      ...song,
      selected: idx === index,
    }));
    setSongs(updatedSongs);
  
    try {
      const response = await fetch(`https://api.moatoverflow.com/v1/song_reel/${songReelId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ selectedSongIndex: index }),
      });
  
      const result = await response.json();
      if (!result.success) {
        console.error("Failed to update selected song index:", result.message);
      }
    } catch (error) {
      console.error("Error updating selected song index:", error);
    }
  };  

  // Generate alignment for the selected song.
  // Now, the alignment is generated automatically and stored in the document's "alignedComments".
  const handleGenerateAlignment = async () => {
    if (!songReelId) {
      setError("Song reel document not loaded.");
      return;
    }
    const selectedSongIndex = songs.findIndex((s) => s.selected);
    if (selectedSongIndex === -1) {
      setError("Please select a song to generate alignment.");
      return;
    }
    try {
      setLoading(true);
      const payload = {
        _id: songReelId,
        songIndex: selectedSongIndex,
        language: "en" // default language; adjust as needed
      };
      const response = await fetch("https://api.moatoverflow.com/v1/song_reel/song-alignment", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success) {
        const updatedSongReel = data.data;
        // Use the automatically generated alignedComments from the document.
        setAlignedComments(updatedSongReel.alignedComments || []);
        setShowAlignmentEditor(true);
      } else {
        setError("Failed to generate alignment.");
      }
    } catch (err) {
      console.error("Error generating alignment:", err);
      setError("Error generating alignment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <h2>Create Song Reel Using Tiktok/Instagram Comments</h2>
      </CardHeader>
      <SongReelForm
        songReelId={songReelId}
        title={title}
        setTitle={setTitle}
        selectedChannel={selectedChannel}
        setSelectedChannel={handleSelectChannel}
        thumbnailBackgrounds={thumbnailBackgrounds}
        thumbnailBackground={thumbnailBackground}
        handleSelectThumbnailBackground={handleSelectThumbnailBackground}
        logo={channelLogo}
        channels={channels}
        platform={platform}
        setPlatform={setPlatform}
        videoId={videoId}
        setVideoId={setVideoId}
        handlePostNewReel={handlePostNewReel}
        loading={loading}
      />
      {reelDetails && <ReelDetailsPreview
        songReelId={songReelId}
        profile_pic={reelDetails?.profile_pic}
        username={reelDetails?.username}
        fullname={reelDetails?.fullname}
        like_count={reelDetails?.like_count}
        comment_count={reelDetails?.comment_count}
        caption={reelDetails?.caption}
        postedAt={reelDetails?.createdAt}
        video_filename={reelDetails?.video_filename}
        thumbnailImage={thumbnailImage}
        setThumbnailImage={setThumbnailImage}
      />}
      <ThumbnailUploader
        songReelId={songReelId}
        thumbnail={finalThumbnail}
        setThumbnail={setFinalThumbnail}
      />
      <CommentsTable
        songReelId={songReelId}
        commentsData={commentsData}
        selectedRows={selectedRows}
        toggleRowSelection={toggleRowSelection}
        platform={platform}
        handleExtractComments={handleExtractComments}
        handleGenerateSongFile={handleGenerateSongFile}
        loading={loading}
      />
      <SongSelector 
        loading={loading}
        isGeneratingSongs={isGeneratingSongs} 
        songReelId={songReelId}
        songs={songs}
        handleSelectSong={handleSelectSong}
        setSongs={setSongs}
        handleGenerateAlignment={handleGenerateAlignment} 
      />
      {showAlignmentEditor && (
        <AlignmentEditor alignedComments={alignedComments} />
      )}
      {error && (
        <CardBody>
          <div className="alert alert-danger">{error}</div>
        </CardBody>
      )}
    <CardFooter>
      <div className="d-flex">
        {showAlignmentEditor && (
          <button className="btn btn-success me-2" onClick={handleGenerateSongReel} disabled={loading}>
            {loading ? "Processing..." : "Generate Song Reel"}
          </button>
        )}
      </div>
    </CardFooter>

    </Card>
  );
};

export default SongReelCreate;
